import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GridActionsCellItem, GridColDef, GridRenderCellParams, GridRowParams, useGridApiContext } from '@mui/x-data-grid-premium';
import DeleteIcon from '@mui/icons-material/Delete';
import { InputAdornment, Stack, TextField } from '@mui/material';

import { tableSettingsProps, useTableSettings, useInitialTableSettings, useCompleteTableSettings } from 'src/core/tables/useTableSettings';
import { useTableSelector, MergeResult } from 'src/core/tables/useTableSelector';
import tableHelper from 'src/core/tables/tableHelper';
import ThumbnailCell from 'src/core/tables/helpers/ThumbnailCell';
import { useGetAllSalesItems } from 'src/pages/products/productRequests';
import { callApi } from 'src/core/api/callApi';
import { getSalesItemPricesBySku } from 'src/core/api/apiRequests';

export function useCampaignSelectorTables(initialValues: any, salesPriceListRows: any[] = EMPTY_ROWS, discountTypeOptions: any[] = EMPTY_ROWS ): readonly [ GridColDef[], tableSettingsProps, GridColDef[], tableSettingsProps, GridColDef[], tableSettingsProps, CampaignTableData, CampaignPriceEditModes, CampaignPriceBulkEditDialogState, ReturnType<typeof useTableSelector>, ReturnType<typeof useTableSelector> ] {
  const [salesItems, setSalesItems] = useState<any[]>([]);
  const [priceLists, setPriceLists] = useState<any[]>([]);
  const [campaignPrices, setCampaignPrices] = useState<any[]>([]);
  const [campaignPriceEditModes, setCampaignPriceEditModes] = useState<CampaignPriceEditModes>({});
  const [latestPricesBySku, setLatestPricesBySku] = useState<Record<string, LatestSalesItemPrice[]>>({});
  const [latestPriceFetchQueue, setLatestPriceFetchQueue] = useState<string[]>([]);
  const [activeLatestSalesItemSku, setActiveLatestSalesItemSku] = useState<string | null>(null);
  const [latestPriceLoading, setLatestPriceLoading] = useState(false);
  const initialSalesItemsKeyRef = useRef<string>('');
  const initialPriceListsKeyRef = useRef<string>('');
  const initialCampaignPricesKeyRef = useRef<string>('');
  const campaignPricesRef = useRef<any[]>([]);
  const campaignPriceEditModesRef = useRef<CampaignPriceEditModes>({});
  const isMountedRef = useRef(true);
  const { rows: salesItemRows, status: salesItemStatus } = useGetAllSalesItems(true);
  useEffect(() => { return () => { isMountedRef.current = false; }; }, []);
  useEffect(() => { campaignPricesRef.current = campaignPrices; }, [campaignPrices]);
  useEffect(() => { campaignPriceEditModesRef.current = campaignPriceEditModes; }, [campaignPriceEditModes]);
  const getCampaignPriceEditMode = useCallback((rowKey: string) => { return campaignPriceEditModesRef.current[String(rowKey)] ?? null; }, []);
  const setCampaignPriceEditMode = useCallback((rowKey: string, editMode: CampaignPriceEditMode) => {
    setCampaignPriceEditModes((prev) => {
      const key = String(rowKey);
      if (prev[key] === editMode) return prev;
      return { ...prev, [key]: editMode };
    });
  }, []);
  const removeCampaignPriceEditModes = useCallback((rowsToRemove: any[]) => {
    const keysToRemove = rowsToRemove.map((row) => campaignPriceRowKey(row)).filter(Boolean);
    if (!keysToRemove.length) return;
    setCampaignPriceEditModes((prev) => {
      if (!keysToRemove.some((key) => key in prev)) return prev;
      const next = { ...prev };
      keysToRemove.forEach((key) => delete next[key]);
      return next;
    });
  }, []);
  const queueLatestSalesItemFetch = useCallback(
    (skus: any[]) => {
      const cleanSkus = skus.map((value) => text(value).trim()).filter(Boolean);
      if (!cleanSkus.length) return;
      setLatestPriceFetchQueue((prev) => {
        const next = [...prev];
        cleanSkus.forEach((value) => {
          if (latestPricesBySku[value]) return;
          if (activeLatestSalesItemSku === value) return;
          if (next.includes(value)) return;
          next.push(value);
        });
        return next.length === prev.length ? prev : next;
      });
    },
    [activeLatestSalesItemSku, latestPricesBySku]
  );
  useEffect(() => {
    if (activeLatestSalesItemSku) return;
    if (!latestPriceFetchQueue.length) return;
    const [nextSku, ...rest] = latestPriceFetchQueue;
    setActiveLatestSalesItemSku(nextSku);
    setLatestPriceFetchQueue(rest);
  }, [activeLatestSalesItemSku, latestPriceFetchQueue]);
  useEffect(() => {
    if (!activeLatestSalesItemSku) return;
    const sku = activeLatestSalesItemSku;
    return fetchSalesItemPricesBySku({ sku, isMountedRef, setLoading: setLatestPriceLoading, onSuccess: (requestSku, rows) => { setLatestPricesBySku((prev) => (prev[requestSku] ? prev : { ...prev, [requestSku]: rows })); setActiveLatestSalesItemSku(null); }, onError: (requestSku) => { setLatestPricesBySku((prev) => (prev[requestSku] ? prev : { ...prev, [requestSku]: EMPTY_ROWS })); setActiveLatestSalesItemSku(null); } });
  }, [activeLatestSalesItemSku]);
  useEffect(() => {
    if (initialValues?.CampaignSalesItems == null) return;
    const nextKey = stableKey(initialValues.CampaignSalesItems);
    if (initialSalesItemsKeyRef.current === nextKey) return;
    initialSalesItemsKeyRef.current = nextKey;
    const nextRows = arr(initialValues.CampaignSalesItems).map((row, index) => normalizeSalesItem(row, initialValues, index));
    setSalesItems(nextRows);
    queueLatestSalesItemFetch(nextRows.map(salesItemSku));
  }, [initialValues?.CampaignSalesItems, initialValues?.Id, queueLatestSalesItemFetch]);
  useEffect(() => {
    if (initialValues?.SalesPriceLists == null) return;
    const nextKey = stableKey(initialValues.SalesPriceLists);
    if (initialPriceListsKeyRef.current === nextKey) return;
    initialPriceListsKeyRef.current = nextKey;
    setPriceLists(arr(initialValues.SalesPriceLists).map(normalizePriceList));
  }, [initialValues?.SalesPriceLists]);
  useEffect(() => {
    if (initialValues?.CampaignPrices == null) return;
    const nextKey = stableKey(initialValues.CampaignPrices);
    if (initialCampaignPricesKeyRef.current === nextKey) return;
    initialCampaignPricesKeyRef.current = nextKey;
    setCampaignPriceEditModes({});
    setCampaignPrices(arr(initialValues.CampaignPrices).map(normalizeCampaignPrice));
  }, [initialValues?.CampaignPrices]);
  const rebuildCampaignPrices = useCallback((nextSalesItems: any[], nextPriceLists: any[], previousPrices: any[] = campaignPricesRef.current) => { return buildCampaignPriceRows(nextSalesItems, nextPriceLists, previousPrices, initialValues, latestPricesBySku, discountTypeOptions); }, [discountTypeOptions, initialValues, latestPricesBySku] );
  const deleteSalesItem = useCallback(
    (row: any) => {
      const siId = salesItemId(row);
      const rowsToRemove = campaignPricesRef.current.filter((price) => String(id(price?.SalesItemsId) ?? id(price?.SalesItemId)) === String(siId));
      removeCampaignPriceEditModes(rowsToRemove);
      setSalesItems((prev) => prev.filter((item) => String(salesItemId(item)) !== String(siId)));
      setCampaignPrices((prev) => prev.filter((price) => String(id(price?.SalesItemsId) ?? id(price?.SalesItemId)) !== String(siId)));
    },
    [removeCampaignPriceEditModes]
  );
  const deletePriceList = useCallback(
    (row: any) => {
      const splId = priceListId(row);
      const rowsToRemove = campaignPricesRef.current.filter((price) => String(id(price?.SalesPriceList) ?? id(price?.SalesPriceListId)) === String(splId));
      removeCampaignPriceEditModes(rowsToRemove);
      setPriceLists((prev) => prev.filter((priceList) => String(priceListId(priceList)) !== String(splId)));
      setCampaignPrices((prev) => prev.filter((price) => String(id(price?.SalesPriceList) ?? id(price?.SalesPriceListId)) !== String(splId)));
    },
    [removeCampaignPriceEditModes]
  );
  const [salesItemDisplayColumns, salesItemDisplayTable] = useCampaignSalesItemsDisplayTable(salesItems, deleteSalesItem);
  const [priceListDisplayColumns, priceListDisplayTable] = useCampaignPriceListsDisplayTable(priceLists, deletePriceList);
  const [campaignPricesColumns, campaignPricesTable, campaignPriceBulkEditDialog] = useCampaignPricesDisplayTable(campaignPrices, setCampaignPrices, latestPriceLoading || Boolean(activeLatestSalesItemSku) || latestPriceFetchQueue.length > 0, getCampaignPriceEditMode, setCampaignPriceEditMode );
  const [salesItemSelectColumns, salesItemSelectTable] = useCampaignSalesItemsSelectTable(arr(salesItemRows), salesItemStatus);
  const [priceListSelectColumns, priceListSelectTable] = useCampaignPriceListsSelectTable(salesPriceListRows);
  const mergeSalesItems = useCallback(
    (prev: any[], picked: any[]): MergeResult<any> => {
      if (!picked?.length) return prev;
      const next = [...prev];
      const skusToFetch: string[] = [];
      picked.forEach((row, index) => {
        const normalized = normalizeSalesItem(row, initialValues, next.length + index);
        const siId = salesItemId(normalized);
        if (!siId || next.some((existing) => String(salesItemId(existing)) === String(siId))) return;
        next.push(normalized);
        skusToFetch.push(salesItemSku(normalized));
      });
      if (skusToFetch.length) queueLatestSalesItemFetch(skusToFetch);
      if (stableKey(prev.map(salesItemId)) !== stableKey(next.map(salesItemId))) setCampaignPrices(rebuildCampaignPrices(next, priceLists));
      return next;
    },
    [initialValues, priceLists, queueLatestSalesItemFetch, rebuildCampaignPrices]
  );
  const mergePriceLists = useCallback(
    (prev: any[], picked: any[]): MergeResult<any> => {
      if (!picked?.length) return prev;
      const next = [...prev];
      picked.forEach((row) => {
        const normalized = normalizePriceList(row);
        const splId = priceListId(normalized);
        if (!splId || next.some((existing) => String(priceListId(existing)) === String(splId))) return;
        next.push(normalized);
      });
      if (stableKey(prev.map(priceListId)) !== stableKey(next.map(priceListId))) setCampaignPrices(rebuildCampaignPrices(salesItems, next));
      return next;
    },
    [rebuildCampaignPrices, salesItems]
  );
  const salesItemSelector = useTableSelector<any>({ rows: salesItems, setRows: setSalesItems, selectGrid: [salesItemSelectColumns, salesItemSelectTable], getId: (row) => salesItemId(row), onMerge: mergeSalesItems, rowId: 'SalesItemId', displayTableSettings: salesItemDisplayTable });
  const priceListSelector = useTableSelector<any>({ rows: priceLists, setRows: setPriceLists, selectGrid: [priceListSelectColumns, priceListSelectTable], getId: (row) => priceListId(row), onMerge: mergePriceLists, rowId: 'SalesPriceListId', displayTableSettings: priceListDisplayTable });
  useEffect(() => {
    if (!salesItems.length || !priceLists.length) {
      if (campaignPricesRef.current.length) setCampaignPrices([]);
      return;
    }
    const next = rebuildCampaignPrices(salesItems, priceLists);
    if (priceStableKey(campaignPricesRef.current) !== priceStableKey(next)) setCampaignPrices(next);
  }, [salesItems, priceLists, latestPricesBySku, rebuildCampaignPrices]);
  const campaignTableData = useMemo<CampaignTableData>(() => ({ salesItems, priceLists, campaignPrices, campaignPriceEditModes }), [salesItems, priceLists, campaignPrices, campaignPriceEditModes] );
  return [salesItemDisplayColumns, salesItemDisplayTable, priceListDisplayColumns, priceListDisplayTable, campaignPricesColumns, campaignPricesTable, campaignTableData, campaignPriceEditModes, campaignPriceBulkEditDialog, salesItemSelector, priceListSelector] as const;
}

const EMPTY_ROWS: any[] = [];

type CampaignPriceEditMode = 'amount' | 'percentage';
type CampaignPriceEditModes = Record<string, CampaignPriceEditMode>;
export type CampaignBulkPriceEditKind = 'fixedPrice' | 'discountAmount' | 'discountRate';
export type CampaignBulkPriceEdit = { kind: CampaignBulkPriceEditKind; value: number };
export type CampaignPriceBulkEditDialogState = { open: boolean; selectedCount: number; onClose: () => void; onApply: (edit: CampaignBulkPriceEdit) => void };
type CampaignTableData = { salesItems: any[]; priceLists: any[]; campaignPrices: any[]; campaignPriceEditModes?: CampaignPriceEditModes };
type LatestSalesItemPrice = { SalesAssortment?: string; SalesPriceList?: string; PriceExcTax?: number; PriceIncTax?: number; Currency?: string };

const arr = (value: any) => (Array.isArray(value) ? value : EMPTY_ROWS);
const primitiveValue = (value: any) => (value && typeof value === 'object' && ('value' in value || 'Value' in value) ? value.value ?? value.Value : value);
const num = (value: any, fallback = 0) => {
  const n = Number(String(primitiveValue(value) ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : fallback;
};
const round = (value: any, dp = 2) => {
  const factor = Math.pow(10, dp);
  return Math.round(num(value) * factor) / factor;
};
const clamp = (value: any, min: number, max: number) => Math.min(Math.max(num(value, min), min), max);
const key = (value: any) => (value && typeof value === 'object' ? value.Key ?? value.key ?? value.value ?? value.Id ?? null : value);
const id = (value: any) => {
  const n = Number(key(value));
  return Number.isFinite(n) ? n : null;
};
const str = (value: any) => (value && typeof value === 'object' ? String(value.Value ?? value.value ?? value.label ?? value.Name ?? '') : String(value ?? ''));
const text = (...values: any[]) => values.map(str).find((value) => value !== '' && value !== '[object Object]') ?? '';
const kv = (keyValue: any, textValue: any) => ({ Key: keyValue ?? null, Value: text(textValue) });
const salesItemId = (row: any) => id(row?.SalesItemId) ?? id(row?.SalesItemsId) ?? id(row?.SalesItem) ?? id(row?.SalesItem_Key);
const priceListId = (row: any) => id(row?.SalesPriceList) ?? id(row?.SalesPriceListId) ?? id(row?.SalesPriceList_Key) ?? id(row?.Id);
const campaignId = (initialValues: any) => id(initialValues?.Id) ?? id(initialValues?.CampaignId) ?? 0;
const salesItemSku = (row: any) => text(row?.SalesItemNumber, row?.ArticleNumber, row?.Sku);
const comboId = (priceListIdValue: any, salesItemIdValue: any) => `${priceListIdValue ?? 'no-price-list'}_${salesItemIdValue ?? 'no-sales-item'}`;
const stableKey = (rows: any[]) => JSON.stringify(rows ?? []);
const priceStableKey = (rows: any[]) => JSON.stringify(arr(rows).map((row) => ({ id: row?.CampaignPriceRowId, item: row?.SalesItemsId ?? row?.SalesItemId, list: row?.SalesPriceList?.Key ?? row?.SalesPriceListId, original: row?.OriginalPrice, campaign: row?.CampaignPrice, discount: row?.DiscountValue, disabled: row?.Disabled })) );
const priceListNameKey = (value: any) => text(value).trim().toLowerCase();
const responseRows = (data: any) => (Array.isArray(data?.Items) ? data.Items : Array.isArray(data) ? data : EMPTY_ROWS);
const hasCampaignPrice = (value: any) => value !== null && value !== undefined && value !== '' && Number.isFinite(num(value, NaN));

const DISCOUNT_AMOUNT_TYPE = kv(2, 'Discount Amount');
const DISCOUNT_PERCENTAGE_TYPE = kv(3, 'Discount Percentage');

const parseNumLoose = (value: any) => {
  const raw = primitiveValue(value);
  if (raw == null) return NaN;
  const cleaned = String(raw).replace(',', '.').trim();
  if (cleaned === '') return NaN;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : NaN;
};

const stripLeadingZeroIfMultiDigit = (value: string) => (/^0\d+$/.test(value) ? String(Number(value)) : value);
const formatNumber = (value: number | undefined | null, dp = 2) => (!Number.isFinite(value as number) ? '' : String(round(Number(value), dp)));

const campaignPriceEditModeFromTypeId = (typeIdValue: any): CampaignPriceEditMode | null => {
  const typeId = id(typeIdValue);
  if (typeId === 2) return 'amount';
  if (typeId === 3) return 'percentage';
  return null;
};

const campaignPriceEditModeFromRow = (row: any): CampaignPriceEditMode | null => {
  const rawType = String(row?.CampaignPriceEditType ?? row?.CampaignDiscountEditType ?? row?.DiscountEditType ?? '').toLowerCase();
  if (rawType === 'amount' || rawType === 'value' || rawType === 'fixed') return 'amount';
  if (rawType === 'percentage' || rawType === 'percent' || rawType === 'rate') return 'percentage';
  return campaignPriceEditModeFromTypeId(row?.DiscountType) ?? campaignPriceEditModeFromTypeId(row?.DiscountType_Key);
};

const campaignPriceDiscountType = (editType: CampaignPriceEditMode) => (editType === 'amount' ? DISCOUNT_AMOUNT_TYPE : DISCOUNT_PERCENTAGE_TYPE);

const campaignPriceRowKey = (row: any, fallback?: any) => String(row?.CampaignPriceRowId ?? fallback ?? comboId(id(row?.SalesPriceList) ?? id(row?.SalesPriceListId), id(row?.SalesItemsId) ?? id(row?.SalesItemId)));

const campaignPriceSelectionIds = (selectionModel: any, rows: any[] = EMPTY_ROWS) => {
  if (Array.isArray(selectionModel)) return selectionModel;
  if (selectionModel?.ids instanceof Set) {
    const ids = Array.from(selectionModel.ids);
    if (selectionModel.type === 'exclude') {
      const excluded = new Set(ids.map(String));
      return arr(rows).map((row) => campaignPriceRowKey(row)).filter((rowId) => !excluded.has(String(rowId)));
    }
    return ids;
  }
  return EMPTY_ROWS;
};

const clampCampaignPrice = (originalPrice: number, campaignPrice: any) => {
  const value = num(campaignPrice, originalPrice);
  if (!Number.isFinite(value)) return originalPrice;
  if (value < 0) return 0;
  if (originalPrice > 0 && value > originalPrice) return originalPrice;
  return round(value, 2);
};

const campaignPriceFromDiscountAmount = (originalPrice: any, discountAmount: any) => {
  const original = round(originalPrice, 2);
  const amount = clamp(discountAmount, 0, original);
  return clampCampaignPrice(original, original - amount);
};

const campaignPriceFromDiscountPercentage = (originalPrice: any, discountPercentage: any) => {
  const original = round(originalPrice, 2);
  const percentage = clamp(discountPercentage, 0, 100);
  return clampCampaignPrice(original, original * (1 - percentage / 100));
};

const discountAmountFromCampaignPrice = (originalPrice: any, campaignPrice: any) => {
  const original = round(originalPrice, 2);
  const campaign = clampCampaignPrice(original, campaignPrice);
  return round(Math.max(0, original - campaign), 2);
};

const discountPercentageFromCampaignPrice = (originalPrice: any, campaignPrice: any) => {
  const original = round(originalPrice, 2);
  const campaign = clampCampaignPrice(original, campaignPrice);
  if (!original || original <= 0) return 0;
  return round(Math.max(0, Math.min(100, ((original - campaign) / original) * 100)), 2);
};

const updateCampaignPriceDerivedFields = (row: any, campaignPriceInput?: any, editTypeInput?: CampaignPriceEditMode) => {
  const originalPrice = round(row?.OriginalPrice ?? 0, 2);
  const editType = editTypeInput ?? campaignPriceEditModeFromRow(row) ?? 'percentage';
  const campaignPrice = clampCampaignPrice(originalPrice, campaignPriceInput ?? row?.CampaignPrice ?? originalPrice);
  const discountAmount = discountAmountFromCampaignPrice(originalPrice, campaignPrice);
  const discountPercentage = discountPercentageFromCampaignPrice(originalPrice, campaignPrice);
  const discountType = campaignPriceDiscountType(editType);
  return { ...row, OriginalPrice: originalPrice, CampaignPrice: campaignPrice, CampaignPriceEditType: editType, DiscountType: discountType, DiscountType_Key: discountType.Key, DiscountType_Value: discountType.Value, DiscountValue: editType === 'amount' ? discountAmount : discountPercentage, DiscountAmount: discountAmount, DiscountPercentage: discountPercentage, DiscountBasedOn: row?.DiscountBasedOn ?? 'ListPrice', LPDiscountAmount: discountAmount, LPDiscountRate: originalPrice ? round(discountAmount / originalPrice, 4) : 0 };
};

const campaignPriceValueGetter = (valueOrParams: any, rowMaybe?: any) => {
  const value = rowMaybe === undefined ? valueOrParams?.value ?? valueOrParams?.row?.CampaignPrice ?? valueOrParams : valueOrParams;
  const raw = primitiveValue(value);
  const parsed = parseNumLoose(raw);
  return Number.isFinite(parsed) ? parsed : raw;
};

const campaignPriceValueParser = (value: any) => num(value, 0);

const campaignDiscountAmountValueGetter = (valueOrParams: any, rowMaybe?: any) => {
  const row = rowMaybe ?? valueOrParams?.row ?? {};
  return row?.DiscountAmount ?? row?.LPDiscountAmount ?? discountAmountFromCampaignPrice(row?.OriginalPrice, row?.CampaignPrice);
};

const campaignDiscountPercentageValueGetter = (valueOrParams: any, rowMaybe?: any) => {
  const row = rowMaybe ?? valueOrParams?.row ?? {};
  return row?.DiscountPercentage ?? discountPercentageFromCampaignPrice(row?.OriginalPrice, row?.CampaignPrice);
};

function CampaignPriceEditCellInline({ params, onEditModeChange }: { params: GridRenderCellParams; onEditModeChange?: (rowKey: string, editMode: CampaignPriceEditMode) => void }) {
  const apiRef = useGridApiContext();
  const rowId = params.id;
  const field = params.field;
  const hasFocus = (params as any).hasFocus ?? false;
  const getLiveRow = () => apiRef?.current?.getRow?.(rowId) ?? params.row ?? {};
  const liveRow = getLiveRow();
  const live = updateCampaignPriceDerivedFields(liveRow);
  const initialMode = campaignPriceEditModeFromRow(liveRow) ?? 'amount';
  const [mode, setMode] = React.useState<CampaignPriceEditMode>(initialMode);
  const [amountInput, setAmountInput] = React.useState<string>(formatNumber(live.CampaignPrice, 2));
  const [percentInput, setPercentInput] = React.useState<string>(formatNumber(live.DiscountPercentage, 2));
  useEffect(() => {
    const liveRow = getLiveRow();
    const row = updateCampaignPriceDerivedFields(liveRow);
    setMode(campaignPriceEditModeFromRow(liveRow) ?? 'amount');
    setAmountInput(formatNumber(row.CampaignPrice, 2));
    setPercentInput(formatNumber(row.DiscountPercentage, 2));
  }, [rowId]);
  const setGridCampaignPrice = (campaignPrice: number, editType: CampaignPriceEditMode) => {
    const row = getLiveRow();
    onEditModeChange?.(campaignPriceRowKey(row, rowId), editType);
    apiRef?.current?.setEditCellValue?.({ id: rowId, field: 'CampaignPrice', value: campaignPrice, debounceMs: 0 });
  };

  const handleAmountChange = (rawValue: string) => {
    const nextInput = stripLeadingZeroIfMultiDigit(rawValue ?? '');
    const row = getLiveRow();
    const originalPrice = round(row?.OriginalPrice ?? 0, 2);
    const campaignPrice = clampCampaignPrice(originalPrice, parseNumLoose(nextInput));
    const discountPercentage = discountPercentageFromCampaignPrice(originalPrice, campaignPrice);
    setMode('amount');
    setAmountInput(nextInput);
    setPercentInput(formatNumber(discountPercentage, 2));
    setGridCampaignPrice(campaignPrice, 'amount');
  };

  const handlePercentageChange = (rawValue: string) => {
    const nextInput = stripLeadingZeroIfMultiDigit(rawValue ?? '');
    const row = getLiveRow();
    const originalPrice = round(row?.OriginalPrice ?? 0, 2);
    const campaignPrice = campaignPriceFromDiscountPercentage(originalPrice, parseNumLoose(nextInput));
    setMode('percentage');
    setPercentInput(nextInput);
    setAmountInput(formatNumber(campaignPrice, 2));
    setGridCampaignPrice(campaignPrice, 'percentage');
  };

  const stopEdit = (commit: boolean) => apiRef?.current?.stopCellEditMode?.({ id: rowId, field, ignoreModifications: !commit });
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') stopEdit(true);
    if (event.key === 'Escape') stopEdit(false);
  };

  return (
    <Stack spacing={0.5} sx={{ width: '100%', minHeight: 62, justifyContent: 'center', py: 0.25 }}>
      <TextField value={amountInput} onFocus={() => setMode('amount')} onChange={(event) => handleAmountChange(event.target.value)} onKeyDown={handleKeyDown} size="small" variant="outlined" type="text" inputMode="decimal" placeholder="Value" fullWidth autoFocus={hasFocus && mode === 'amount'} sx={{ '& fieldset': { border: 'none' }, '& .MuiInputBase-root': { height: 28 } }} />
      <TextField value={percentInput} onFocus={() => setMode('percentage')} onChange={(event) => handlePercentageChange(event.target.value)} onKeyDown={handleKeyDown} size="small" variant="outlined" type="text" inputMode="decimal" placeholder="%" fullWidth autoFocus={hasFocus && mode === 'percentage'} sx={{ '& fieldset': { border: 'none' }, '& .MuiInputBase-root': { height: 28 } }} InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
    </Stack>
  );
}

export const useCampaignPricesDisplayTable = (rows: any[], setRows: React.Dispatch<React.SetStateAction<any[]>>, loading?: boolean, getEditMode?: (rowKey: string) => CampaignPriceEditMode | null, onEditModeChange?: (rowKey: string, editMode: CampaignPriceEditMode) => void ): readonly [GridColDef[], tableSettingsProps, CampaignPriceBulkEditDialogState] => {
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkEditRowIds, setBulkEditRowIds] = useState<any[]>([]);
  const setExternalRows = useCallback((newRows: any[]) => { setRows(Array.isArray(newRows) ? newRows : []); }, [setRows] );
  const initialTableSettings = useInitialTableSettings({ tableName: 'CampaignPricesTable', idPath: ['CampaignPriceRowId'], setExternalRows }, rows);
  const apiRef = initialTableSettings.apiRef;
  const rowSelectionModel = initialTableSettings.rowSelectionModel;
  const processRowUpdateRef = initialTableSettings.processRowUpdateRef;
  const selectedCampaignPriceRowIds = useMemo(() => campaignPriceSelectionIds(rowSelectionModel, rows).filter((rowId) => String(rowId) !== 'multi-edit-row'), [rowSelectionModel, rows] );
  const selectedCampaignPriceRowCount = selectedCampaignPriceRowIds.length;
  const isBulkEditMode = selectedCampaignPriceRowCount > 1;
  const getCurrentSelectedRowIds = useCallback(() => {
    const rowIds = campaignPriceSelectionIds(rowSelectionModel, rows).filter((rowId) => String(rowId) !== 'multi-edit-row');
    const apiSelectedRows = apiRef?.current?.getSelectedRows?.();
    if (!apiSelectedRows?.size) return rowIds;
    const merged = new Map<string, any>();
    rowIds.forEach((rowId) => merged.set(String(rowId), rowId));
    apiSelectedRows.forEach((_, rowId) => {
      if (String(rowId) !== 'multi-edit-row') merged.set(String(rowId), rowId);
    });
    return Array.from(merged.values());
  }, [apiRef, rowSelectionModel, rows]);
  const campaignPriceValueSetter = useCallback(
    (valueOrParams: any, rowMaybe?: any) => {
      const value = rowMaybe === undefined ? valueOrParams?.value : valueOrParams;
      const row = rowMaybe === undefined ? valueOrParams?.row : rowMaybe;
      const rowKey = campaignPriceRowKey(row, valueOrParams?.id);
      const editType = getEditMode?.(rowKey) ?? campaignPriceEditModeFromRow(row) ?? 'percentage';
      return updateCampaignPriceDerivedFields(row, value, editType);
    },
    [getEditMode]
  );
  const openCampaignPriceBulkEdit = useCallback(
    (event?: React.MouseEvent | React.KeyboardEvent) => {
      const rowIds = getCurrentSelectedRowIds();
      if (rowIds.length <= 1) return;
      event?.preventDefault?.();
      event?.stopPropagation?.();
      setBulkEditRowIds(rowIds);
      setBulkEditOpen(true);
    },
    [getCurrentSelectedRowIds]
  );
  const closeCampaignPriceBulkEdit = useCallback(() => { setBulkEditOpen(false); }, []);
  const applyCampaignPriceBulkEdit = useCallback(
    (edit: CampaignBulkPriceEdit) => {
      const api = apiRef?.current;
      const selectedIdSet = new Set(bulkEditRowIds.map((rowId) => String(rowId)));
      const updates = rows .filter((row) => selectedIdSet.has(String(campaignPriceRowKey(row))))
        .map((row) => {
          const originalPrice = round(row?.OriginalPrice ?? 0, 2);
          const campaignPrice =
            edit.kind === 'fixedPrice'
              ? clampCampaignPrice(originalPrice, edit.value)
              : edit.kind === 'discountAmount'
              ? campaignPriceFromDiscountAmount(originalPrice, edit.value)
              : campaignPriceFromDiscountPercentage(originalPrice, edit.value);
          const editType: CampaignPriceEditMode = edit.kind === 'discountRate' ? 'percentage' : 'amount';
          const updated = updateCampaignPriceDerivedFields(row, campaignPrice, editType);
          onEditModeChange?.(campaignPriceRowKey(row), editType);
          return { row, updated };
        });
      if (!updates.length) return;
      const processRowUpdate = processRowUpdateRef?.current;
      const committedRows = updates.map(({ row, updated }) => processRowUpdate?.(updated, row) ?? updated);
      const committedById = new Map(committedRows.map((row) => [String(campaignPriceRowKey(row)), row]));
      setRows((prev) => arr(prev).map((row) => committedById.get(String(campaignPriceRowKey(row))) ?? row));
      api?.updateRows?.(committedRows);
    },
    [apiRef, bulkEditRowIds, onEditModeChange, processRowUpdateRef, rows, setRows]
  );
  const campaignPriceBulkEditDialog = useMemo<CampaignPriceBulkEditDialogState>(() => ({ open: bulkEditOpen, selectedCount: bulkEditRowIds.length, onClose: closeCampaignPriceBulkEdit, onApply: applyCampaignPriceBulkEdit }), [applyCampaignPriceBulkEdit, bulkEditOpen, bulkEditRowIds.length, closeCampaignPriceBulkEdit] );
  const renderCampaignPriceCell = useCallback(
    (params: GridRenderCellParams) => {
      const canBulkEdit = isBulkEditMode;

      return (
        <span
          tabIndex={canBulkEdit ? 0 : undefined}
          role={canBulkEdit ? 'button' : undefined}
          onDoubleClick={(event) => {
            if (!canBulkEdit) return;
            openCampaignPriceBulkEdit(event);
          }}
          onKeyDown={(event) => {
            if (!canBulkEdit || event.key !== 'Enter') return;
            openCampaignPriceBulkEdit(event);
          }}
          style={{ display: 'block', width: '100%', height: '100%', cursor: canBulkEdit ? 'pointer' : undefined }}
        >
          {params.formattedValue ?? params.value ?? ''}
        </span>
      );
    },
    [isBulkEditMode, openCampaignPriceBulkEdit]
  );
  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'ArticleNumber', headerName: 'Article Number', width: 160 },
      { field: 'Name', headerName: 'Name', flex: 1, minWidth: 260 },
      { field: 'OriginalPrice', headerName: 'Original Price', type: 'number', width: 150 },
      { field: 'CampaignPrice', headerName: 'Campaign Price', type: 'number', width: 220, editable: !isBulkEditMode, valueGetter: campaignPriceValueGetter, valueParser: campaignPriceValueParser, valueSetter: campaignPriceValueSetter as any, renderCell: renderCampaignPriceCell, renderEditCell: (params) => <CampaignPriceEditCellInline params={params} onEditModeChange={onEditModeChange} /> },
      { field: 'SalesPriceList_Value', headerName: 'Sales Price List', width: 220, valueGetter: tableHelper.getFieldKeyOrValue },
      { field: 'Currency', headerName: 'Currency', width: 110 },
      { field: 'DiscountAmount', headerName: 'Discount Amount', type: 'number', width: 160, valueGetter: campaignDiscountAmountValueGetter },
      { field: 'DiscountPercentage', headerName: 'Discount %', type: 'number', width: 130, valueGetter: campaignDiscountPercentageValueGetter },
      { field: 'DiscountType_Value', headerName: 'Discount Type', width: 180, valueGetter: tableHelper.getFieldKeyOrValue },
      { field: 'DiscountValue', headerName: 'Submit Discount Value', type: 'number', width: 180 },
      { field: 'DiscountBasedOn', headerName: 'Based On', width: 140 },
      { field: 'SalesAssortment', headerName: 'Sales Assortment', width: 190 },
      { field: 'LPDiscountAmount', headerName: 'LP Discount Amount', type: 'number', width: 180 },
      { field: 'LPDiscountRate', headerName: 'LP Discount Rate', type: 'number', width: 160 },
      { field: 'LandedCost', headerName: 'Landed Cost', type: 'number', width: 140 },
      { field: 'Margin', headerName: 'Margin %', type: 'number', width: 120 },
      { field: 'Manufacturer', headerName: 'Brand', width: 160 },
      { field: 'ManufacturerProductId', headerName: 'Manufacturer Product ID', width: 220 },
      { field: 'ProductType', headerName: 'Product Type', width: 160 },
      { field: 'Disabled', headerName: 'Disabled', type: 'boolean', width: 120 },
      { field: 'Status', headerName: 'Status', width: 110 }
    ],
    [campaignPriceValueSetter, isBulkEditMode, onEditModeChange, renderCampaignPriceCell]
  );
  const tableSettings = useCompleteTableSettings({ columns }, initialTableSettings);
  tableSettings.autoHeight = true;
  tableSettings.loading = Boolean(loading);
  tableSettings.hasToolbar = false;
  tableSettings.density = 80;
  tableSettings.checkboxSelection = true;
  tableSettings.disableSelectionOnClick = true;
  return [columns, tableSettings, campaignPriceBulkEditDialog] as const;
};
const fetchSalesItemPricesBySku = ({ sku, isMountedRef, setLoading, onSuccess, onError }: { sku: string; isMountedRef: React.MutableRefObject<boolean>; setLoading: React.Dispatch<React.SetStateAction<boolean>>; onSuccess: (sku: string, rows: LatestSalesItemPrice[]) => void; onError: (sku: string) => void; }) => {
  let cancelled = false;
  const cancelRequest = callApi({
    request: getSalesItemPricesBySku(sku),
    config: {
      isMountedRef: () => isMountedRef.current && !cancelled,
      logLevel: ['error'],
      setLoadingState: setLoading,
      resolve: (data: any) => {
        if (cancelled) return;
        onSuccess(sku, responseRows(data));
      },
      reject: () => {
        if (cancelled) return;
        onError(sku);
      }
    }
  });
  return () => {
    cancelled = true;
    cancelRequest?.();
  };
};

const normalizePriceList = (row: any) => {
  const splId = priceListId(row);
  const splName = text(row?.SalesPriceList, row?.SalesPriceListName, row?.Name);
  return {
    ...row,
    SalesPriceListId: splId,
    SalesPriceListName: splName,
    Organisation: row?.Organisation && typeof row.Organisation === 'object' ? row.Organisation : kv(id(row?.Organisation), text(row?.Organisation, row?.Organisation_Value)),
    OrganisationAssortment:
      row?.OrganisationAssortment && typeof row.OrganisationAssortment === 'object'
        ? row.OrganisationAssortment
        : kv(id(row?.OrganisationAssortment), text(row?.OrganisationAssortment, row?.OrganisationAssortment_Value)),
    SalesAssortment:
      row?.SalesAssortment && typeof row.SalesAssortment === 'object' ? row.SalesAssortment : kv(id(row?.SalesAssortment), text(row?.SalesAssortment, row?.SalesAssortment_Value)),
    SalesPriceList: row?.SalesPriceList && typeof row.SalesPriceList === 'object' ? row.SalesPriceList : kv(splId, splName),
    SalesPriceListCurrencyName: text(row?.SalesPriceListCurrencyName, row?.Currency, row?.Currency_Value),
    SalesPriceListTaxIncluded: Boolean(row?.SalesPriceListTaxIncluded ?? row?.TaxIncluded ?? false)
  };
};

const normalizeSalesItem = (row: any, initialValues: any, index = 0) => {
  const siId = salesItemId(row);
  return { ...row, Id: row?.Id ?? row?.CampaignSalesItemId ?? -Math.abs(siId || index + 1), CampaignId: row?.CampaignId ?? campaignId(initialValues), SalesText: row?.SalesText ?? '', MaxQuantityPerOrder: row?.MaxQuantityPerOrder ?? 0, MaxQuantityPerCustomer: row?.MaxQuantityPerCustomer ?? 0, HideStandardPrice: Boolean(row?.HideStandardPrice ?? false), SalesItemId: siId, ManufacturerProductId: text(row?.ManufacturerProductId), ManufacturerName: text(row?.ManufacturerName, row?.Manufacturer, row?.Manufacturer_Value), ProductTypeId: id(row?.ProductTypeId) ?? id(row?.ProductType) ?? id(row?.ProductType_Key), ProductType: text(row?.ProductType, row?.ProductType_Value), ProductLine: text(row?.ProductLine), SalesItemName: text(row?.SalesItemName, row?.Name, row?.SalesItem, row?.SalesItem_Value), SalesItemNumber: text(row?.SalesItemNumber, row?.ArticleNumber, row?.Sku) };
};

const normalizeCampaignPrice = (row: any) => ({
  ...row,
  CampaignPriceRowId: row?.CampaignPriceRowId ?? comboId(id(row?.SalesPriceList) ?? id(row?.SalesPriceListId), id(row?.SalesItemsId) ?? id(row?.SalesItemId)),
  DiscountType:
    row?.DiscountType && typeof row.DiscountType === 'object'
      ? row.DiscountType
      : kv(id(row?.DiscountType) ?? id(row?.DiscountType_Key) ?? 3, text(row?.DiscountType, row?.DiscountType_Value, 'Discount Percentage')),
  SalesPriceList:
    row?.SalesPriceList && typeof row.SalesPriceList === 'object'
      ? row.SalesPriceList
      : kv(id(row?.SalesPriceListId) ?? id(row?.SalesPriceList_Key), text(row?.SalesPriceList, row?.SalesPriceList_Value))
});

const defaultDiscountType = (existing: any, discountTypeOptions: any[]) => {
  if (existing?.DiscountType) return existing.DiscountType;
  const option = arr(discountTypeOptions).find((option) => text(option?.label, option?.Name, option?.Value).toLowerCase().includes('percentage'));
  return option ? kv(option.value ?? option.Id, option.label ?? option.Name) : kv(3, 'Discount Percentage');
};

const getLatestPriceForItem = (latestPricesBySku: Record<string, LatestSalesItemPrice[]>, priceList: any, salesItem: any) => {
  const sku = salesItemSku(salesItem);
  const splName = priceListNameKey(text(priceList?.SalesPriceList, priceList?.SalesPriceListName, priceList?.Name));
  return arr(latestPricesBySku[sku]).find((row) => priceListNameKey(row?.SalesPriceList) === splName);
};

const getOriginalPrice = (latestPriceRow: any, existing: any) => round(latestPriceRow?.PriceIncTax ?? existing?.OriginalPrice ?? 0, 2);

const buildCampaignPriceRows = (salesItems: any[], priceLists: any[], previousPrices: any[], initialValues: any, latestPricesBySku: Record<string, LatestSalesItemPrice[]>, discountTypeOptions: any[] ) => {
  const previousByCombo = new Map<string, any>();
  arr(previousPrices).forEach((row) => previousByCombo.set(comboId(id(row?.SalesPriceList) ?? id(row?.SalesPriceListId), id(row?.SalesItemsId) ?? id(row?.SalesItemId)), row));
  return priceLists.flatMap((priceList) => {
    const splId = priceListId(priceList);
    if (!splId) return [];
    return salesItems
      .map((salesItem) => {
        const siId = salesItemId(salesItem);
        if (!siId) return null;
        const existing = previousByCombo.get(comboId(splId, siId));
        const latestPriceRow = getLatestPriceForItem(latestPricesBySku, priceList, salesItem);
        const originalPrice = getOriginalPrice(latestPriceRow, existing);
        const campaignPrice = round(hasCampaignPrice(existing?.CampaignPrice) ? existing.CampaignPrice : originalPrice, 2);
        const lpDiscountAmount = round(Math.max(0, originalPrice - campaignPrice), 2);
        const lpDiscountRate = originalPrice ? round(lpDiscountAmount / originalPrice, 4) : 0;
        const editType = campaignPriceEditModeFromRow(existing) ?? 'percentage';
        return normalizeCampaignPrice({ ...existing, CampaignPriceRowId: comboId(splId, siId), CampaignId: existing?.CampaignId ?? campaignId(initialValues), SalesItemsId: siId, ArticleNumber: salesItemSku(salesItem), Name: text(salesItem?.SalesItemName, salesItem?.Name), CampaignSalesAssortmentItemId: existing?.CampaignSalesAssortmentItemId ?? null, OrganisationAssortmentsId: existing?.OrganisationAssortmentsId ?? id(priceList?.OrganisationAssortment) ?? null, OriginalPrice: originalPrice, CampaignPrice: campaignPrice, LandedCost: round(existing?.LandedCost ?? 0, 2), DiscountType: defaultDiscountType(existing, discountTypeOptions), DiscountValue: editType === 'amount' ? lpDiscountAmount : discountPercentageFromCampaignPrice(originalPrice, campaignPrice), DiscountBasedOn: existing?.DiscountBasedOn ?? 'ListPrice', SalesAssortment: text(latestPriceRow?.SalesAssortment, priceList?.SalesAssortment, priceList?.SalesAssortment_Value), Currency: text(latestPriceRow?.Currency, priceList?.SalesPriceListCurrencyName, priceList?.Currency, priceList?.Currency_Value), Margin: existing?.Margin ?? 0, SalesPriceList: kv(splId, text(priceList?.SalesPriceList, priceList?.SalesPriceListName, priceList?.Name)), Disabled: existing?.Disabled ?? false, Status: existing?.Status ?? 1, ManufacturerProductId: text(salesItem?.ManufacturerProductId), Manufacturer: text(salesItem?.ManufacturerName, salesItem?.Manufacturer, salesItem?.Manufacturer_Value), ProductType: text(salesItem?.ProductType, salesItem?.ProductType_Value), Tax: existing?.Tax ?? 0, MarketPriceNoG: existing?.MarketPriceNoG ?? 0, MPDiscountAmount: existing?.MPDiscountAmount ?? 0, MPDiscountRate: existing?.MPDiscountRate ?? 0, LPDiscountAmount: lpDiscountAmount, LPDiscountRate: lpDiscountRate, SalesText: existing?.SalesText ?? salesItem?.SalesText ?? '', CampaignSalesItemDisabled: existing?.CampaignSalesItemDisabled ?? salesItem?.Disabled ?? false });
      })
      .filter(Boolean);
  });
};

export const useCampaignSalesItemsSelectTable = (salesItemRows: any[], salesItemStatus: any): readonly [GridColDef[], tableSettingsProps] => {
  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'actions', getActions: (params) => tableHelper.buttonColumn(params, 'Edit', '/products/sales-items/edit?id=', 'ArticleNumber', 'Edit Sales Item', true), type: 'actions', headerName: 'Edit', minWidth: 80, maxWidth: 120, flex: 0.8 },
      { field: 'ArticleNumber', headerName: 'Sku', width: 110, hideable: false },
      { field: 'Name', headerName: 'Name', flex: 1, minWidth: 220 },
      { field: 'ImageResourceId', headerName: 'Thumb', width: 100, cellClassName: 'thumbnail-cell', renderCell: (params) => <ThumbnailCell params={params} /> },
      { field: 'Status_Value', headerName: 'Status', valueGetter: tableHelper.getFieldKeyOrValue, renderCell: tableHelper.renderStatusCell, width: 140 },
      { field: 'AvailableQty', headerName: 'Stock', type: 'number', width: 100 },
      { field: 'ProductType_Value', headerName: 'Product Type', valueGetter: tableHelper.getFieldKeyOrValue, width: 170 },
      { field: 'Manufacturer_Value', headerName: 'Brand', valueGetter: tableHelper.getFieldKeyOrValue, width: 170 },
      { field: 'ManufacturerProductId', headerName: 'Manufacturer Product Id', width: 220 },
      { field: 'ProductLine', headerName: 'Product Line', width: 170 },
      { field: 'SalesItemId', headerName: 'Sales Item Id', width: 140 }
    ],
    []
  );
  const tableSettings = useTableSettings({ columns, rows: arr(salesItemRows), status: salesItemStatus, idPath: ['SalesItemId'], tableName: 'CampaignSalesItemsSelectTable' });
  tableSettings.hasToolbar = false;
  tableSettings.autoHeight = false;
  tableSettings.checkboxSelection = true;
  return [columns, tableSettings] as const;
};

export const useCampaignPriceListsSelectTable = (priceListRows: any[]): readonly [GridColDef[], tableSettingsProps] => {
  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'Id', headerName: 'Sales Price List ID', width: 160, hideable: false },
      { field: 'Name', headerName: 'Sales Price List', flex: 1, minWidth: 220 },
      { field: 'Organisation', headerName: 'Organisation', width: 180 },
      { field: 'OrganisationAssortment', headerName: 'Organisation Assortment', width: 230 },
      { field: 'SalesAssortment', headerName: 'Sales Assortment', width: 210 },
      { field: 'Currency', headerName: 'Currency', width: 120 },
      { field: 'TaxIncluded', headerName: 'Tax Included', width: 140, type: 'boolean' }
    ],
    []
  );
  const tableSettings = useTableSettings({ columns, rows: arr(priceListRows), idPath: ['Id'], tableName: 'CampaignPriceListsSelectTable' });
  tableSettings.hasToolbar = false;
  tableSettings.autoHeight = false;
  tableSettings.checkboxSelection = true;
  tableSettings.loading = false;
  return [columns, tableSettings] as const;
};

export const useCampaignSalesItemsDisplayTable = (rows: any[], onDelete: (row: any) => void): readonly [GridColDef[], tableSettingsProps] => {
  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'actions', type: 'actions', headerName: 'Delete', width: 80, getActions: (params: GridRowParams) => [<GridActionsCellItem key="delete" icon={<DeleteIcon />} label="Delete" onClick={() => onDelete(params.row)} />] },
      { field: 'SalesItemId', headerName: 'Sales Item ID', width: 140 },
      { field: 'SalesItemNumber', headerName: 'Article Number', width: 160 },
      { field: 'SalesItemName', headerName: 'Name', flex: 1, minWidth: 240 },
      { field: 'ManufacturerName', headerName: 'Brand', width: 170 },
      { field: 'ManufacturerProductId', headerName: 'Manufacturer Product ID', width: 220 },
      { field: 'ProductType', headerName: 'Product Type', width: 170 },
      { field: 'ProductLine', headerName: 'Product Line', width: 170 },
      { field: 'HideStandardPrice', headerName: 'Hide Std Price', type: 'boolean', width: 150 },
      { field: 'MaxQuantityPerOrder', headerName: 'Max / Order', type: 'number', width: 140 },
      { field: 'MaxQuantityPerCustomer', headerName: 'Max / Customer', type: 'number', width: 160 }
    ],
    [onDelete]
  );
  const tableSettings = useTableSettings({ columns, rows, idPath: ['SalesItemId'], tableName: 'CampaignSalesItemsTable' });
  tableSettings.autoHeight = true;
  tableSettings.loading = false;
  tableSettings.hasToolbar = false;
  return [columns, tableSettings] as const;
};

export const useCampaignPriceListsDisplayTable = (rows: any[], onDelete: (row: any) => void): readonly [GridColDef[], tableSettingsProps] => {
  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'actions', type: 'actions', headerName: 'Delete', width: 80, getActions: (params: GridRowParams) => [<GridActionsCellItem key="delete" icon={<DeleteIcon />} label="Delete" onClick={() => onDelete(params.row)} />] },
      { field: 'SalesPriceList_Key', headerName: 'Sales Price List ID', width: 170, valueGetter: tableHelper.getFieldKeyOrValue },
      { field: 'Organisation_Value', headerName: 'Organisation', width: 180, valueGetter: tableHelper.getFieldKeyOrValue },
      { field: 'OrganisationAssortment_Value', headerName: 'Organisation Assortment', width: 230, valueGetter: tableHelper.getFieldKeyOrValue },
      { field: 'SalesAssortment_Value', headerName: 'Sales Assortment', width: 210, valueGetter: tableHelper.getFieldKeyOrValue },
      { field: 'SalesPriceList_Value', headerName: 'Sales Price List', flex: 1, minWidth: 220, valueGetter: tableHelper.getFieldKeyOrValue },
      { field: 'SalesPriceListCurrencyName', headerName: 'Currency', width: 120 },
      { field: 'SalesPriceListTaxIncluded', headerName: 'Tax Included', width: 140, type: 'boolean' }
    ],
    [onDelete]
  );
  const tableSettings = useTableSettings({ columns, rows, idPath: ['SalesPriceList', 'Key'], tableName: 'SalesPriceListsTable' });
  tableSettings.autoHeight = true;
  tableSettings.loading = false;
  tableSettings.hasToolbar = false;
  return [columns, tableSettings] as const;
};
